import { api } from '../libs/api'; 0
import { getRequestConfig } from '@/utils/auth';

export interface LoginResponse {
  success: boolean;
  data: {
    accessToken: string;
    idToken: string;
  };
  error?: string;
  message?: string;
}

export interface AuthResponse {
  success: boolean;
  message?: string;
  error?: string;
  data?: any;
}

class AuthService {
  private baseUrl = '/auth';
  private storageKey = 'authTokens';

  private setTokens(accessToken: string, idToken: string) {
    localStorage.setItem(this.storageKey, JSON.stringify({
      accessToken,
      idToken
    }));
  }

  private clearTokens() {
    localStorage.removeItem(this.storageKey);
  }

  private getStoredTokens(): { accessToken: string | null, idToken: string | null } {
    const tokens = localStorage.getItem(this.storageKey);
    if (tokens) {
      try {
        return JSON.parse(tokens);
      } catch (e) {
        this.clearTokens();
      }
    }
    return { accessToken: null, idToken: null };
  }

  // Core authentication methods
  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      const response = await api.post(`${this.baseUrl}/login`, {
        email,
        password
      }) as LoginResponse;

      if (response.success) {
        this.setTokens(response.data.accessToken, response.data.idToken);
      }

      return response;
    } catch (error: any) {
      console.error('Login error:', error);
      return {
        success: false,
        data: { accessToken: '', idToken: '' },
        error: 'Authentication failed',
        message: error.message || 'Invalid email or password'
      };
    }
  }

  async logout(): Promise<AuthResponse> {
    try {
      const response = await api.post(`${this.baseUrl}/logout`) as AuthResponse;

      // Clear tokens from localStorage
      this.clearTokens();

      return response;
    } catch (error: any) {
      console.error('Logout error:', error);
      return {
        success: false,
        error: 'Logout failed',
        message: error.message || 'Could not complete logout'
      };
    }
  }

  async register(userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
  }): Promise<AuthResponse> {
    try {
      return await api.post(`${this.baseUrl}/register`, userData) as AuthResponse;
    } catch (error: any) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: 'Registration failed',
        message: error.message || 'Could not create account'
      };
    }
  }

  // Verification methods
  async verifyEmail(email: string, code: string): Promise<AuthResponse> {
    try {
      return await api.post(`${this.baseUrl}/verify/email`, { email, code }) as AuthResponse;
    } catch (error: any) {
      console.error('Email verification error:', error);
      return {
        success: false,
        error: 'Verification failed',
        message: error.message || 'Could not verify email'
      };
    }
  }

  async verifySms(code: string): Promise<AuthResponse> {
    try {
      return await api.post(`${this.baseUrl}/verify/sms`, { code }) as AuthResponse;
    } catch (error: any) {
      console.error('SMS verification error:', error);
      return {
        success: false,
        error: 'Verification failed',
        message: error.message || 'Could not verify phone number'
      };
    }
  }

  // Password management methods
  async forgotPassword(email: string): Promise<AuthResponse> {
    try {
      return await api.post(`${this.baseUrl}/forgot-password`, { email }) as AuthResponse;
    } catch (error: any) {
      console.error('Forgot password error:', error);
      return {
        success: false,
        error: 'Request failed',
        message: error.message || 'Could not process password reset request'
      };
    }
  }

  async resetPassword(email: string, code: string, newPassword: string): Promise<AuthResponse> {
    try {
      return await api.post(`${this.baseUrl}/reset-password`, {
        email,
        code,
        newPassword
      }) as AuthResponse;
    } catch (error: any) {
      console.error('Password reset error:', error);
      return {
        success: false,
        error: 'Reset failed',
        message: error.message || 'Could not reset password'
      };
    }
  }

  // Token management methods
  getAccessToken(): string | null {
    return this.getStoredTokens().accessToken;
  }

  getIdToken(): string | null {
    return this.getStoredTokens().idToken;
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  // Current user method
  async getCurrentUser(): Promise<AuthResponse> {
    try {
      return await api.get(`${this.baseUrl}/me`, getRequestConfig()) as AuthResponse;
    } catch (error: any) {
      console.error('Get current user error:', error);
      return {
        success: false,
        error: 'Request failed',
        message: error.message || 'Could not get current user'
      };
    }
  }
}

// Create and export a singleton instance
export const authService = new AuthService();
export default authService;
